import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Upload, Package, Tags, Receipt, Mail, ImageOff, FileText, Euro, Cpu, AlertTriangle, CheckCircle2, Inbox, LayoutDashboard, Users, Layers, CalendarCheck, ExternalLink, Newspaper, ShoppingCart, Image as ImageIcon, GripVertical, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import EmailAssistant from "@/components/EmailAssistant";
import { AdminOverview, AdminCustomers, AdminOrders, AdminMessages, AdminProducts, AdminFlyerProjects, FlyerMonitor, GlobalSearch, EnvBadge } from "@/components/AdminOffice";
import AdminAbandonedCarts from "@/components/AdminAbandonedCarts";
import { AdminSiteContent, AdminCategories, AdminMedia } from "@/components/AdminCMS";
import { AdminBlog } from "@/components/AdminBlog";
import { productImage, formatPrice } from "@/lib/helpers";
import { toast } from "sonner";

const emptyProduct = {
  title_it: "", title_en: "", description_it: "", description_en: "",
  price: 0, category: "", image_url: "", image_path: "", file_path: "", file_name: "", is_demo: false, active: true,
  product_type: "digital", amazon_kind: "book", featured: false, order: 0,
  seo_title_it: "", seo_title_en: "", seo_desc_it: "", seo_desc_en: "",
  subtitle_it: "", subtitle_en: "", author: "", long_description_it: "", long_description_en: "",
  book_format: "", amazon_url: "", asin: "",
  whats_inside_it: "", whats_inside_en: "", for_whom_it: "", for_whom_en: "", highlights_it: "", highlights_en: "",
  short_description_it: "", short_description_en: "",
  what_you_get_it: [], what_you_get_en: [],
  gallery_images: [], file_meta: null, has_preview: false,
};

const ProductDialog = ({ open, onClose, onSaved, categories, editing }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  useEffect(() => {
    if (editing) setForm({ ...emptyProduct, ...editing, image_url: editing.image_url || "" });
    else setForm(emptyProduct);
    setCreatedId(null);
  }, [editing, open]);

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingImg(true);
    try {
      const d = await upload(file, "image");
      setForm((f) => ({ ...f, image_path: d.storage_path, image_url: "" }));
      toast.success(t("admin.saved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingImg(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingFile(true);
    try {
      const d = await upload(file, "file");
      setForm((f) => ({ ...f, file_path: d.storage_path, file_name: d.original_filename, file_meta: d.meta || null }));
      toast.success(t("admin.saved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingFile(false); }
  };

  const onGalleryImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) { return; }
    if ((form.gallery_images || []).length >= 3) { toast.error("Massimo 3 immagini aggiuntive"); return; }
    setUploadingGallery(true);
    try {
      const d = await upload(file, "image");
      setForm((f) => ({ ...f, gallery_images: [...(f.gallery_images || []), { image_path: d.storage_path, image_url: "" }] }));
      toast.success(t("admin.saved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingGallery(false); e.target.value = ""; }
  };
  const removeGalleryImage = (idx) => setForm((f) => ({ ...f, gallery_images: (f.gallery_images || []).filter((_, i) => i !== idx) }));

  const [dragIdx, setDragIdx] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const reorderGallery = (from, to) => setForm((f) => {
    const arr = [...(f.gallery_images || [])];
    if (from == null || from < 0 || to < 0 || from >= arr.length || to >= arr.length || from === to) return f;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    return { ...f, gallery_images: arr };
  });
  const setAsCover = (i) => setForm((f) => {
    const arr = [...(f.gallery_images || [])];
    const chosen = arr[i];
    if (!chosen) return f;
    const oldCover = { image_url: f.image_url || "", image_path: f.image_path || "" };
    if (!oldCover.image_url && !oldCover.image_path) arr.splice(i, 1);
    else arr[i] = oldCover;
    return { ...f, image_url: chosen.image_url || "", image_path: chosen.image_path || "", gallery_images: arr };
  });

  const generatePreview = async () => {
    setGeneratingPreview(true);
    try {
      let id = editing?.id || createdId;
      // Persist SOLO se prodotto nuovo o se e' stato caricato un nuovo file in questa sessione
      // (evita di sovrascrivere/azzerare il file_path di un prodotto esistente).
      if (!id || form.file_path) { id = await persist(); onSaved(); }
      await api.post(`/admin/products/${id}/preview`);
      setForm((f) => ({ ...f, has_preview: true }));
      toast.success("Anteprima generata (2 pagine)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGeneratingPreview(false); }
  };
  const deletePreview = async () => {
    const id = editing?.id || createdId;
    if (!id) { setForm((f) => ({ ...f, has_preview: false })); return; }
    setGeneratingPreview(true);
    try {
      await api.delete(`/admin/products/${id}/preview`);
      setForm((f) => ({ ...f, has_preview: false }));
      toast.success("Anteprima eliminata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGeneratingPreview(false); }
  };
  const isPdfFile = (form.file_meta?.format === "PDF") || (form.file_name || "").toLowerCase().endsWith(".pdf");

  const updateWyg = (lg, idx, val) => setForm((f) => {
    const key = `what_you_get_${lg}`;
    const arr = [...(f[key] || [])]; arr[idx] = val;
    return { ...f, [key]: arr };
  });
  const addWyg = (lg) => setForm((f) => ({ ...f, [`what_you_get_${lg}`]: [...(f[`what_you_get_${lg}`] || []), ""] }));
  const removeWyg = (lg, idx) => setForm((f) => ({ ...f, [`what_you_get_${lg}`]: (f[`what_you_get_${lg}`] || []).filter((_, i) => i !== idx) }));

  const validate = () => {
    if (!form.title_it || !form.title_en || !form.category) {
      toast.error("Compila Titolo IT/EN e Categoria");
      return false;
    }
    if (form.product_type === "amazon" && !form.amazon_url) {
      toast.error("Per i prodotti Amazon inserisci l'URL Amazon");
      return false;
    }
    return true;
  };

  const persist = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0, image_url: form.image_url || null,
      image_path: form.image_path || null, file_path: form.file_path || null, file_name: form.file_name || null,
      what_you_get_it: (form.what_you_get_it || []).map((s) => s.trim()).filter(Boolean),
      what_you_get_en: (form.what_you_get_en || []).map((s) => s.trim()).filter(Boolean),
      gallery_images: form.gallery_images || [] };
    const targetId = editing?.id || createdId;
    if (targetId) { await api.put(`/admin/products/${targetId}`, payload); return targetId; }
    const { data } = await api.post("/admin/products", payload);
    setCreatedId(data.id);
    return data.id;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await persist();
      toast.success(t("admin.saved"));
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openPreview = async () => {
    if (!validate()) return;
    setPreviewing(true);
    try {
      const id = await persist();
      onSaved();
      window.open(`/prodotti/${id}`, "_blank", "noopener,noreferrer");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPreviewing(false); }
  };

  const preview = productImage(form);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? t("admin.editProduct") : t("admin.newProduct")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-brand-light p-3 flex flex-wrap items-center gap-4">
            <div className="min-w-[200px]">
              <Label className="text-xs">Tipo prodotto</Label>
              <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })}>
                <SelectTrigger data-testid="pf-product-type" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="digital">Digitale (scaricabile)</SelectItem>
                  <SelectItem value="amazon">Libro / Prodotto Amazon</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2"><Switch data-testid="pf-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /> <span className="text-sm text-brand-navy">In evidenza</span></label>
            <label className="flex items-center gap-2"><Switch data-testid="pf-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> <span className="text-sm text-brand-navy">Visibile</span></label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t("admin.titleIt")}</Label><Input data-testid="pf-title-it" value={form.title_it} onChange={(e) => setForm({ ...form, title_it: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("admin.titleEn")}</Label><Input data-testid="pf-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>{t("admin.descIt")}</Label><Textarea data-testid="pf-desc-it" rows={2} value={form.description_it} onChange={(e) => setForm({ ...form, description_it: e.target.value })} className="mt-1.5" /></div>
          <div><Label>{t("admin.descEn")}</Label><Textarea data-testid="pf-desc-en" rows={2} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{form.product_type === "amazon" ? "Prezzo indicativo (facoltativo)" : t("admin.price")}</Label><Input data-testid="pf-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label>{t("admin.category")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pf-category" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name_it}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t("admin.uploadImage")}</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="h-16 w-16 rounded-lg overflow-hidden bg-brand-light border shrink-0">
                  {preview ? <img src={preview} alt="" className="w-full h-full object-contain p-1" /> : <div className="w-full h-full flex items-center justify-center text-brand-navy/30"><ImageOff className="h-5 w-5" /></div>}
                </div>
                <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-4 py-2 text-sm text-brand-navy hover:border-brand-electric">
                  <Upload className="h-4 w-4" /> {uploadingImg ? t("common.loading") : t("admin.uploadImage")}
                  <input data-testid="pf-image-upload" type="file" accept="image/*" className="hidden" onChange={onImage} />
                </label>
              </div>
              <Input data-testid="pf-image-url" placeholder={t("admin.imageUrl")} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value, image_path: "" })} className="mt-2" />
            </div>
            {form.product_type !== "amazon" && (
            <div>
              <Label>{t("admin.uploadFile")}</Label>
              <div className="mt-1.5">
                <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-4 py-2 text-sm text-brand-navy hover:border-brand-electric">
                  <FileText className="h-4 w-4" /> {uploadingFile ? t("common.loading") : t("admin.uploadFile")}
                  <input data-testid="pf-file-upload" type="file" className="hidden" onChange={onFile} />
                </label>
                {form.file_name && <p className="mt-2 text-xs text-brand-navy/60 truncate">{form.file_name}</p>}
              </div>
            </div>
            )}
          </div>

          {form.product_type !== "amazon" && (
            <div className="space-y-4 rounded-xl border border-brand-electric/30 bg-brand-electric/5 p-4" data-testid="pf-digital-fields">
              <p className="text-sm font-semibold text-brand-navy">Dettagli prodotto digitale (facoltativi)</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">Descrizione breve IT (2-3 righe)</Label><Textarea data-testid="pf-short-it" rows={2} value={form.short_description_it} onChange={(e) => setForm({ ...form, short_description_it: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="text-xs">Descrizione breve EN (2-3 righe)</Label><Textarea data-testid="pf-short-en" rows={2} value={form.short_description_en} onChange={(e) => setForm({ ...form, short_description_en: e.target.value })} className="mt-1.5" /></div>
              </div>

              <div>
                <Label className="text-xs">Immagini aggiuntive (max 3)</Label>
                <div className="mt-1.5 flex flex-wrap items-start gap-3">
                  {(form.gallery_images || []).map((g, i) => (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div draggable
                        onDragStart={() => setDragIdx(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); reorderGallery(dragIdx, i); setDragIdx(null); }}
                        onDragEnd={() => setDragIdx(null)}
                        className={`relative h-16 w-16 rounded-lg overflow-hidden border bg-brand-light cursor-move transition-opacity ${dragIdx === i ? "opacity-40 ring-2 ring-brand-electric" : ""}`}
                        data-testid={`pf-gallery-thumb-${i}`} title="Trascina per riordinare">
                        <img src={productImage(g)} alt="" className="w-full h-full object-contain p-1 pointer-events-none" />
                        <button type="button" onClick={() => setAsCover(i)} data-testid={`pf-set-cover-${i}`} title="Imposta come copertina" className="absolute top-0 left-0 bg-brand-electric text-white px-1 py-0.5 rounded-br"><ImageIcon className="h-3 w-3" /></button>
                        <span className="absolute bottom-0 left-0 bg-brand-navy/60 text-white text-[9px] leading-4 px-1 rounded-tr flex items-center gap-0.5"><GripVertical className="h-2.5 w-2.5" />{i + 1}</span>
                        <button type="button" onClick={() => removeGalleryImage(i)} data-testid={`pf-gallery-remove-${i}`} className="absolute top-0 right-0 bg-red-500 text-white px-1.5 text-xs leading-5 rounded-bl">×</button>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => reorderGallery(i, i - 1)} disabled={i === 0} data-testid={`pf-gallery-left-${i}`}
                          className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-brand-navy/20 text-brand-navy hover:border-brand-electric disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Sposta indietro">
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => reorderGallery(i, i + 1)} disabled={i === (form.gallery_images || []).length - 1} data-testid={`pf-gallery-right-${i}`}
                          className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-brand-navy/20 text-brand-navy hover:border-brand-electric disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Sposta avanti">
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {(form.gallery_images || []).length < 3 && (
                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-4 py-2 text-sm text-brand-navy hover:border-brand-electric">
                      <Upload className="h-4 w-4" /> {uploadingGallery ? t("common.loading") : "Aggiungi immagine"}
                      <input data-testid="pf-gallery-upload" type="file" accept="image/*" className="hidden" onChange={onGalleryImage} />
                    </label>
                  )}
                </div>
                <p className="mt-1 text-xs text-brand-navy/50">La copertina principale resta quella caricata sopra. Qui puoi aggiungere fino a 3 anteprime extra. Trascina le miniature o usa le frecce per cambiarne l'ordine.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {["it", "en"].map((lg) => (
                  <div key={lg}>
                    <Label className="text-xs">Cosa riceverai {lg.toUpperCase()}</Label>
                    <div className="mt-1.5 space-y-2">
                      {(form[`what_you_get_${lg}`] || []).map((v, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input data-testid={`pf-wyg-${lg}-${i}`} value={v} onChange={(e) => updateWyg(lg, i, e.target.value)} placeholder="Es. 1 PDF di 30 pagine" />
                          <button type="button" onClick={() => removeWyg(lg, i)} data-testid={`pf-wyg-remove-${lg}-${i}`} className="text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={() => addWyg(lg)} data-testid={`pf-wyg-add-${lg}`} className="rounded-full text-xs gap-1"><Plus className="h-3 w-3" /> Aggiungi voce</Button>
                    </div>
                  </div>
                ))}
              </div>

              {form.file_meta && (
                <div className="rounded-lg bg-white border border-brand-navy/10 p-3 text-xs text-brand-navy/70" data-testid="pf-file-meta">
                  <span className="font-semibold text-brand-navy">File caricato: </span>
                  {form.file_meta.format || "—"}
                  {form.file_meta.pages ? ` · ${form.file_meta.pages} pagine` : ""}
                  {form.file_meta.size_bytes ? ` · ${(form.file_meta.size_bytes / 1024 / 1024).toFixed(2)} MB` : ""}
                </div>
              )}

              {isPdfFile && (
                <div className="rounded-lg bg-white border border-brand-navy/10 p-3" data-testid="pf-preview-controls">
                  <p className="text-xs font-semibold text-brand-navy">Anteprima PDF pubblica (prime 2 pagine, con filigrana)</p>
                  {form.has_preview ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-green-600 font-semibold" data-testid="pf-preview-available">Anteprima disponibile</span>
                      <Button type="button" variant="outline" size="sm" onClick={generatePreview} disabled={generatingPreview} data-testid="pf-regen-preview" className="rounded-full text-xs">{generatingPreview ? "..." : "Rigenera"}</Button>
                      <Button type="button" variant="outline" size="sm" onClick={deletePreview} disabled={generatingPreview} data-testid="pf-delete-preview" className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50">Elimina</Button>
                      {(editing?.id || createdId) && <a href={`${API}/products/${editing?.id || createdId}/preview.pdf`} target="_blank" rel="noopener noreferrer" data-testid="pf-open-preview-pdf" className="text-xs text-brand-electric underline">Apri anteprima</a>}
                    </div>
                  ) : (
                    <Button type="button" variant="outline" size="sm" onClick={generatePreview} disabled={generatingPreview} data-testid="pf-generate-preview" className="mt-2 rounded-full text-xs gap-1"><FileText className="h-3 w-3" /> {generatingPreview ? "Generazione..." : "Genera anteprima dalle prime 2 pagine"}</Button>
                  )}
                  <p className="mt-1 text-[11px] text-brand-navy/50">L'anteprima contiene SOLO 2 pagine con filigrana e non espone mai il file completo. Il prodotto resta salvato automaticamente prima della generazione.</p>
                </div>
              )}
            </div>
          )}

          {form.product_type === "amazon" && (
            <div className="space-y-4 rounded-xl border border-[#FF9900]/40 bg-[#FF9900]/5 p-4" data-testid="pf-amazon-fields">
              <p className="text-sm font-semibold text-brand-navy">Dettagli libro / prodotto Amazon</p>
              <div className="min-w-[220px]">
                <Label className="text-xs">Tipo elemento Amazon</Label>
                <Select value={form.amazon_kind || "book"} onValueChange={(v) => setForm({ ...form, amazon_kind: v })}>
                  <SelectTrigger data-testid="pf-amazon-kind" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="book">Libro</SelectItem>
                    <SelectItem value="product">Prodotto fisico</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-brand-navy/50">Determina i testi mostrati nella scheda pubblica (es. "Cosa trovi nel libro" o "Cosa trovi nel prodotto").</p>
              </div>
              {(form.amazon_kind || "book") === "book" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Sottotitolo IT</Label><Input data-testid="pf-subtitle-it" value={form.subtitle_it} onChange={(e) => setForm({ ...form, subtitle_it: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Sottotitolo EN</Label><Input data-testid="pf-subtitle-en" value={form.subtitle_en} onChange={(e) => setForm({ ...form, subtitle_en: e.target.value })} className="mt-1.5" /></div>
              </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(form.amazon_kind || "book") === "book" && (
                  <div><Label>Autore</Label><Input data-testid="pf-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="mt-1.5" /></div>
                )}
                {(form.amazon_kind || "book") === "book" && (
                <div>
                  <Label>Formato</Label>
                  <Select value={form.book_format || "none"} onValueChange={(v) => setForm({ ...form, book_format: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="pf-book-format" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="kindle">Kindle</SelectItem>
                      <SelectItem value="paperback">Cartaceo</SelectItem>
                      <SelectItem value="both">Entrambi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}
                <div><Label>ASIN (facoltativo)</Label><Input data-testid="pf-asin" value={form.asin} onChange={(e) => setForm({ ...form, asin: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div><Label>URL Amazon *</Label><Input data-testid="pf-amazon-url" placeholder="https://www.amazon.it/dp/..." value={form.amazon_url} onChange={(e) => setForm({ ...form, amazon_url: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Descrizione completa IT</Label><Textarea data-testid="pf-long-it" rows={3} value={form.long_description_it} onChange={(e) => setForm({ ...form, long_description_it: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Descrizione completa EN</Label><Textarea data-testid="pf-long-en" rows={3} value={form.long_description_en} onChange={(e) => setForm({ ...form, long_description_en: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>{(form.amazon_kind || "book") === "product" ? "Cosa trovi nel prodotto IT" : "Cosa trovi nel libro IT"}</Label><Textarea data-testid="pf-whats-it" rows={2} value={form.whats_inside_it} onChange={(e) => setForm({ ...form, whats_inside_it: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{(form.amazon_kind || "book") === "product" ? "Cosa trovi nel prodotto EN" : "Cosa trovi nel libro EN"}</Label><Textarea data-testid="pf-whats-en" rows={2} value={form.whats_inside_en} onChange={(e) => setForm({ ...form, whats_inside_en: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Per chi è IT</Label><Textarea data-testid="pf-forwhom-it" rows={2} value={form.for_whom_it} onChange={(e) => setForm({ ...form, for_whom_it: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Per chi è EN</Label><Textarea data-testid="pf-forwhom-en" rows={2} value={form.for_whom_en} onChange={(e) => setForm({ ...form, for_whom_en: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Punti principali IT (uno per riga)</Label><Textarea data-testid="pf-highlights-it" rows={3} value={form.highlights_it} onChange={(e) => setForm({ ...form, highlights_it: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Punti principali EN (uno per riga)</Label><Textarea data-testid="pf-highlights-en" rows={3} value={form.highlights_en} onChange={(e) => setForm({ ...form, highlights_en: e.target.value })} className="mt-1.5" /></div>
              </div>
            </div>
          )}

          <div className="space-y-3 rounded-xl border border-brand-navy/10 p-4" data-testid="pf-seo-fields">
            <p className="text-sm font-semibold text-brand-navy">SEO (facoltativo)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">SEO Title IT</Label><Input data-testid="pf-seo-title-it" value={form.seo_title_it} onChange={(e) => setForm({ ...form, seo_title_it: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">SEO Title EN</Label><Input data-testid="pf-seo-title-en" value={form.seo_title_en} onChange={(e) => setForm({ ...form, seo_title_en: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">SEO Description IT</Label><Textarea data-testid="pf-seo-desc-it" rows={2} value={form.seo_desc_it} onChange={(e) => setForm({ ...form, seo_desc_it: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">SEO Description EN</Label><Textarea data-testid="pf-seo-desc-en" rows={2} value={form.seo_desc_en} onChange={(e) => setForm({ ...form, seo_desc_en: e.target.value })} className="mt-1" /></div>
            </div>
          </div>

          {form.product_type !== "amazon" && (
          <div className="flex items-center gap-3 pt-2">
            <Switch data-testid="pf-is-demo" checked={form.is_demo} onCheckedChange={(v) => setForm({ ...form, is_demo: v })} />
            <Label>{t("admin.isDemo")}</Label>
          </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={openPreview} disabled={previewing || saving}
            className="rounded-full text-brand-navy gap-2 mr-auto" data-testid="pf-open-preview">
            <ExternalLink className="h-4 w-4" /> {previewing ? t("common.loading") : ((editing?.id || createdId) ? "Apri anteprima" : "Salva e apri anteprima")}
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-full text-brand-navy" data-testid="pf-cancel">{t("admin.cancel")}</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white" data-testid="pf-save">{saving ? t("common.loading") : t("admin.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Admin = () => {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [productReload, setProductReload] = useState(0);
  const [newCat, setNewCat] = useState({ slug: "", name_it: "", name_en: "" });
  const [pricing, setPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({ cv_premium_price: "", currency: "eur" });
  const [flyerForm, setFlyerForm] = useState({ flyer_price: "", flyer_currency: "eur" });
  const [flyerSaving, setFlyerSaving] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [budgetForm, setBudgetForm] = useState("");
  const [budgetSaving, setBudgetSaving] = useState(false);

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/cv/usage");
      setUsage(data);
      setBudgetForm(data?.budget?.monthly_budget_eur ? String(data.budget.monthly_budget_eur) : "");
    } catch { /* non-admin */ }
  }, []);

  const saveBudget = async () => {
    setBudgetSaving(true);
    try {
      await api.put("/admin/settings/ai-budget", { monthly_budget_eur: parseFloat(budgetForm || "0") });
      toast.success(t("admin.aiBudgetSaved"));
      await loadUsage();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBudgetSaving(false); }
  };

  const loadPricing = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings/pricing");
      setPricing(data);
      setPricingForm({ cv_premium_price: data.cv_premium_price, currency: data.currency });
      setFlyerForm({ flyer_price: data.flyer_price, flyer_currency: data.flyer_currency });
    } catch { /* non-admin */ }
  }, []);

  const saveFlyerPricing = async () => {
    setFlyerSaving(true);
    try {
      const { data } = await api.put("/admin/settings/pricing/flyer", {
        flyer_price: parseFloat(flyerForm.flyer_price),
        flyer_currency: flyerForm.flyer_currency,
      });
      setPricing(data);
      setFlyerForm({ flyer_price: data.flyer_price, flyer_currency: data.flyer_currency });
      toast.success(t("admin.saved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFlyerSaving(false); }
  };

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      const { data } = await api.put("/admin/settings/pricing", {
        cv_premium_price: parseFloat(pricingForm.cv_premium_price),
        currency: pricingForm.currency, packages: pricing?.packages || {},
      });
      setPricing(data);
      toast.success(t("admin.saved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPricingSaving(false); }
  };

  const loadAll = useCallback(async () => {
    const [p, c] = await Promise.all([
      api.get("/products"), api.get("/categories"),
    ]);
    setProducts(p.data); setCategories(c.data);
  }, []);

  useEffect(() => { loadAll(); loadPricing(); loadUsage(); }, [loadAll, loadPricing, loadUsage]);

  const [tab, setTab] = useState("overview");
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [openOrderId, setOpenOrderId] = useState(null);
  const panelRef = useRef(null);
  const scrollToPanel = () => { requestAnimationFrame(() => panelRef.current?.scrollIntoView({ block: "start", behavior: "auto" })); };
  const changeTab = (v) => { setTab(v); scrollToPanel(); };
  const goCustomer = (id) => { setOpenCustomerId(id); setTab("customers"); scrollToPanel(); };
  const goOrder = (id) => { setOpenOrderId(id); setTab("orders"); scrollToPanel(); };

  const delProduct = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success(t("admin.deleted")); loadAll(); setProductReload((k) => k + 1);
  };

  const addCategory = async () => {
    try {
      await api.post("/admin/categories", newCat);
      toast.success(t("admin.saved")); setNewCat({ slug: "", name_it: "", name_en: "" }); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delCategory = async (id) => { await api.delete(`/admin/categories/${id}`); toast.success(t("admin.deleted")); loadAll(); };

  const uploadCategoryImage = async (id, file, inputEl) => {
    if (inputEl) inputEl.value = "";
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/categories/${id}/image`, fd);
      toast.success("Foto categoria aggiornata");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
  };

  return (
    <div data-testid="admin-page">
      <section className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">{t("admin.title")}</h1>
            <EnvBadge />
            <a
              data-testid="admin-booking-link"
              href="https://booking-app-158.emergent.host/admin/login"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:ml-auto inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold text-sm px-4 py-2 transition-colors"
            >
              <CalendarCheck className="h-4 w-4" /> ADMIN BOOKING <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <div className="mt-5"><GlobalSearch onOpenCustomer={goCustomer} onOpenOrder={goOrder} /></div>
        </div>
      </section>

      <div ref={panelRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-10 scroll-mt-20">
        <div data-testid="admin-restart-preview" className="mb-6 rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-brand-navy">Percorso "Restart Dating 45+" — anteprima senza acquisto:</span>
          <a href="/percorsi/restart-dating-45?admin_preview=digital" target="_blank" rel="noopener noreferrer" data-testid="admin-btn-preview-digital" className="text-sm rounded-full px-4 py-2 bg-brand-navy text-white">Anteprima Digital</a>
          <a href="/percorsi/restart-dating-45?admin_preview=premium" target="_blank" rel="noopener noreferrer" data-testid="admin-btn-preview-premium" className="text-sm rounded-full px-4 py-2 bg-brand-electric text-white">Anteprima Premium</a>
          <span className="text-xs text-brand-navy/50">Non crea ordini, pagamenti o conversioni.</span>
        </div>
        <Tabs value={tab} onValueChange={changeTab}>
          <TabsList className="bg-white border border-brand-navy/10 rounded-full p-1 h-auto flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><LayoutDashboard className="h-4 w-4" /> Panoramica</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Users className="h-4 w-4" /> Clienti e contatti</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Package className="h-4 w-4" /> {t("admin.products")}</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Tags className="h-4 w-4" /> {t("admin.categories")}</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Newspaper className="h-4 w-4" /> Contenuti sito</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><FileText className="h-4 w-4" /> Blog</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><ImageIcon className="h-4 w-4" /> Media</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Receipt className="h-4 w-4" /> {t("admin.orders")}</TabsTrigger>
            <TabsTrigger value="abandoned" data-testid="tab-abandoned" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><ShoppingCart className="h-4 w-4" /> Carrelli abbandonati</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Mail className="h-4 w-4" /> {t("admin.messages")}</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Euro className="h-4 w-4" /> {t("admin.pricing")}</TabsTrigger>
            <TabsTrigger value="ai-costs" data-testid="tab-ai-costs" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Cpu className="h-4 w-4" /> {t("admin.aiCosts")}</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Inbox className="h-4 w-4" /> Assistente Email</TabsTrigger>
            <TabsTrigger value="flyer" data-testid="tab-flyer" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Layers className="h-4 w-4" /> Progetti Flyer</TabsTrigger>
          </TabsList>

          {/* PANORAMICA */}
          <TabsContent value="overview" className="mt-6">
            <AdminOverview onOpenOrder={goOrder} />
          </TabsContent>

          {/* CLIENTI */}
          <TabsContent value="customers" className="mt-6">
            <AdminCustomers openId={openCustomerId} onConsumeOpen={() => setOpenCustomerId(null)} />
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <AdminProducts
              reloadKey={productReload}
              onNew={() => { setEditing(null); setDialogOpen(true); }}
              onEdit={(id) => { const p = products.find((x) => x.id === id); if (p) { setEditing(p); setDialogOpen(true); } }}
              onDelete={delProduct}
            />
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="mt-6">
            <AdminCategories />
          </TabsContent>

          {/* CONTENUTI SITO */}
          <TabsContent value="content" className="mt-6">
            <AdminSiteContent />
          </TabsContent>

          {/* BLOG */}
          <TabsContent value="blog" className="mt-6">
            <AdminBlog />
          </TabsContent>

          {/* MEDIA */}
          <TabsContent value="media" className="mt-6">
            <AdminMedia />
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6">
            <AdminOrders openId={openOrderId} onConsumeOpen={() => setOpenOrderId(null)} />
          </TabsContent>

          <TabsContent value="abandoned" className="mt-6">
            <AdminAbandonedCarts />
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-6">
            <AdminMessages />
          </TabsContent>

          {/* PRICING */}
          <TabsContent value="pricing" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6 items-start">
            <div className="rounded-2xl border border-brand-navy/10 bg-white p-6" data-testid="admin-pricing">
              <h3 className="font-heading font-bold text-lg text-brand-navy">{t("admin.pricingTitle")}</h3>
              <p className="mt-1 text-sm text-brand-navy/60">{t("admin.pricingDesc")}</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("admin.cvPremiumPrice")}</Label>
                  <Input data-testid="pricing-price" type="number" step="0.01" min="0" value={pricingForm.cv_premium_price} disabled={!pricing}
                    onChange={(e) => setPricingForm({ ...pricingForm, cv_premium_price: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>{t("admin.currency")}</Label>
                  <Select value={pricingForm.currency} onValueChange={(v) => setPricingForm({ ...pricingForm, currency: v })} disabled={!pricing}>
                    <SelectTrigger data-testid="pricing-currency" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(pricing?.currencies || ["eur", "usd", "gbp", "chf"]).map((c) => (
                        <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="mt-4 text-xs text-brand-navy/50">{t("admin.pricingNote")}</p>
              <Button data-testid="pricing-save" onClick={savePricing} disabled={pricingSaving || !pricing}
                className="mt-6 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">
                {pricingSaving ? t("common.loading") : t("admin.save")}
              </Button>
            </div>

            <div className="rounded-2xl border border-brand-navy/10 bg-white p-6" data-testid="admin-pricing-flyer">
              <h3 className="font-heading font-bold text-lg text-brand-navy">Prezzi Social &amp; Flyer Creator</h3>
              <p className="mt-1 text-sm text-brand-navy/60">Prezzo del servizio Social &amp; Flyer Creator (3 formati). I nuovi checkout useranno questo valore.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Prezzo</Label>
                  <Input data-testid="flyer-pricing-price" type="number" step="0.01" min="0" value={flyerForm.flyer_price} disabled={!pricing}
                    onChange={(e) => setFlyerForm({ ...flyerForm, flyer_price: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>{t("admin.currency")}</Label>
                  <Select value={flyerForm.flyer_currency} onValueChange={(v) => setFlyerForm({ ...flyerForm, flyer_currency: v })} disabled={!pricing}>
                    <SelectTrigger data-testid="flyer-pricing-currency" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(pricing?.currencies || ["eur", "usd", "gbp", "chf"]).map((c) => (
                        <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="mt-4 text-xs text-brand-navy/50">Il prezzo è salvato lato server e usato dai nuovi acquisti. L'add-on "logo rapido" non è modificabile da qui.</p>
              <Button data-testid="flyer-pricing-save" onClick={saveFlyerPricing} disabled={flyerSaving || !pricing}
                className="mt-6 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">
                {flyerSaving ? t("common.loading") : t("admin.save")}
              </Button>
            </div>
            </div>
          </TabsContent>

          <TabsContent value="ai-costs" className="mt-6">
            <div data-testid="admin-ai-costs">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-bold text-lg text-brand-navy">{t("admin.aiCostsTitle")}</h3>
                {(() => {
                  const h = typeof window !== "undefined" ? window.location.hostname : "";
                  const isPreview = h.includes("preview") || h.includes("emergentagent");
                  return (
                    <span data-testid="ai-env-badge" className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${isPreview ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {isPreview ? "Preview" : "Produzione"}
                    </span>
                  );
                })()}
              </div>
              <p className="mt-1 text-sm text-brand-navy/60 max-w-2xl">{t("admin.aiCostsDesc")}</p>
              <p data-testid="ai-env-note" className="mt-2 text-xs text-brand-navy/50 max-w-2xl">
                I contatori mostrano i dati dell’ambiente attualmente aperto. Preview e sito pubblicato utilizzano archivi separati e i relativi conteggi non vengono sommati.
              </p>

              {!usage ? (
                <div className="mt-6 text-sm text-brand-navy/50">{t("common.loading")}</div>
              ) : (
                <>
                  {/* Suddivisione per funzione + Assistente Email */}
                  <div data-testid="ai-feature-breakdown" className="mt-6 grid sm:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-brand-navy/10 bg-white p-4">
                      <div className="text-xs text-brand-navy/50">CV Smart</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.by_feature?.cv_smart?.count ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">€{Number(usage.by_feature?.cv_smart?.cost_eur ?? 0).toFixed(4)}</div>
                    </div>
                    <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-4" data-testid="ai-logo-card">
                      <div className="text-xs text-brand-navy/50">Logo (Flyer)</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.flyer_logo?.images ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">€{Number(usage.flyer_logo?.cost_eur ?? 0).toFixed(4)} · {usage.flyer_logo?.cost_basis ?? "n/d"}</div>
                    </div>
                    <div className="rounded-xl border border-brand-electric/30 bg-brand-electric/5 p-4" data-testid="ai-flyergen-card">
                      <div className="text-xs text-brand-navy/50">Generazione Flyer</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.flyer_generate?.images ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">€{Number(usage.flyer_generate?.cost_eur ?? 0).toFixed(4)} · {usage.flyer_generate?.cost_basis ?? "n/d"}</div>
                    </div>
                    <div className="rounded-xl border border-brand-navy/10 bg-white p-4" data-testid="ai-flyermod-card">
                      <div className="text-xs text-brand-navy/50">Modifica Flyer</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.flyer_modify?.images ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">€{Number(usage.flyer_modify?.cost_eur ?? 0).toFixed(4)} · {usage.flyer_modify?.cost_basis ?? "n/d"}</div>
                    </div>
                    <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-4" data-testid="ai-direction-card">
                      <div className="text-xs text-brand-navy/50">Direzione creativa</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.flyer_direction?.count ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">gpt-5.6-sol · €{Number(usage.flyer_direction?.cost_eur ?? 0).toFixed(4)} · stima</div>
                    </div>
                    <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-4" data-testid="ai-qccreative-card">
                      <div className="text-xs text-brand-navy/50">Controllo qualità creativo</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.flyer_qc_creative?.count ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">gpt-5.6-sol · €{Number(usage.flyer_qc_creative?.cost_eur ?? 0).toFixed(4)} · stima</div>
                    </div>
                    <div className="rounded-xl border border-brand-navy/10 bg-brand-navy/[0.03] p-4" data-testid="ai-email-card">
                      <div className="text-xs text-brand-navy/50">Assistente Email</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.email_assistant?.total?.total_calls ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">chiamate · token/costo: N/D</div>
                    </div>
                    <div className="rounded-xl border border-brand-navy/10 bg-brand-navy/[0.03] p-4">
                      <div className="text-xs text-brand-navy/50">Totale complessivo</div>
                      <div className="text-2xl font-bold text-brand-navy">{usage.by_feature?.total?.count ?? 0}</div>
                      <div className="text-xs text-brand-navy/50">€{Number(usage.by_feature?.total?.cost_eur ?? 0).toFixed(4)}</div>
                    </div>
                  </div>
                  {usage.flyer_generate && usage.flyer_generate.count > 0 && (
                    <div data-testid="ai-flyergen-detail" className="mt-3 rounded-xl border border-brand-electric/20 bg-white p-4">
                      <div className="text-sm font-semibold text-brand-navy mb-1">Generazione Flyer — creatività</div>
                      <div className="text-xs text-brand-navy/60 mb-2">Provider: <b>OpenAI</b> · immagini: <b>{usage.flyer_generate.images}</b> · costo: <b>€{Number(usage.flyer_generate.cost_eur).toFixed(4)}</b> ({usage.flyer_generate.cost_basis}) · {usage.flyer_generate.real_count} reali · {usage.flyer_generate.estimated_count} stima</div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead><tr className="text-left text-brand-navy/50 border-b border-brand-navy/10">
                            <th className="py-1 pr-3">Modello</th><th className="py-1 pr-3">Formato</th><th className="py-1 pr-3">Tent.</th><th className="py-1 pr-3">QC</th><th className="py-1 pr-3">Costo</th><th className="py-1 pr-3">Progetto</th>
                          </tr></thead>
                          <tbody>
                            {usage.flyer_generate.recent.map((r, i) => (
                              <tr key={i} data-testid={`ai-flyergen-row-${i}`} className="border-b border-brand-navy/5">
                                <td className="py-1 pr-3 text-brand-navy">{r.model}</td>
                                <td className="py-1 pr-3 text-brand-navy/60">{r.format}{r.used_fallback ? " (fallback)" : ""}</td>
                                <td className="py-1 pr-3 text-brand-navy/60">{r.attempt}</td>
                                <td className="py-1 pr-3 font-semibold text-brand-navy">{r.qc_score}{r.qc_blocking_ok ? "✓" : "⚠"}</td>
                                <td className="py-1 pr-3 text-brand-electric">€{Number(r.cost_eur).toFixed(4)}{r.estimated ? "*" : ""}</td>
                                <td className="py-1 pr-3 text-brand-navy/40 font-mono">{String(r.project_id || "").slice(-6)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-1 text-[11px] text-brand-navy/50">{usage.flyer_generate.note}</p>
                    </div>
                  )}
                  {usage.flyer_logo && usage.flyer_logo.count > 0 && (
                    <div data-testid="ai-logo-detail" className="mt-3 rounded-xl border border-brand-orange/20 bg-white p-4">
                      <div className="text-sm font-semibold text-brand-navy mb-1">Logo — Social &amp; Flyer Creator</div>
                      <div className="text-xs text-brand-navy/60">Provider: <b>OpenAI</b> · Modello: <b>gpt-image-1</b> · Costo: <b>{usage.flyer_logo.cost_basis}</b> ({usage.flyer_logo.real_count} reali · {usage.flyer_logo.estimated_count} stima)</div>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                        <div className="rounded-lg bg-brand-navy/[0.03] px-3 py-2"><div className="text-xs text-brand-navy/50">Creazione</div><div className="text-brand-navy">Immagini: <b>{usage.flyer_logo.generate?.images ?? 0}</b> · €{Number(usage.flyer_logo.generate?.cost_eur ?? 0).toFixed(4)}</div></div>
                        <div className="rounded-lg bg-brand-navy/[0.03] px-3 py-2"><div className="text-xs text-brand-navy/50">Rigenerazione</div><div className="text-brand-navy">Immagini: <b>{usage.flyer_logo.revise?.images ?? 0}</b> · €{Number(usage.flyer_logo.revise?.cost_eur ?? 0).toFixed(4)}</div></div>
                        <div className="rounded-lg bg-brand-navy/[0.03] px-3 py-2"><div className="text-xs text-brand-navy/50">Totale logo</div><div className="text-brand-navy">Immagini: <b>{usage.flyer_logo.images ?? 0}</b> · €{Number(usage.flyer_logo.cost_eur ?? 0).toFixed(4)}</div></div>
                      </div>
                      <p className="mt-2 text-[11px] text-brand-navy/50">{usage.flyer_logo.note}</p>
                    </div>
                  )}
                  {usage.email_assistant && (
                    <div className="mt-3 rounded-xl border border-brand-navy/10 bg-white p-4" data-testid="ai-email-detail">
                      <div className="text-sm font-semibold text-brand-navy mb-2">Assistente Email — dettaglio</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                        {[["Oggi", usage.email_assistant.today], ["Mese", usage.email_assistant.month], ["Totale", usage.email_assistant.total]].map(([lbl, s]) => (
                          <div key={lbl} className="rounded-lg bg-brand-navy/[0.03] px-3 py-2">
                            <div className="text-xs text-brand-navy/50">{lbl}</div>
                            <div className="text-brand-navy">Chiamate: <b>{s?.total_calls ?? 0}</b></div>
                            <div className="text-xs text-brand-navy/60">Nuove email: {s?.initial_analysis ?? 0} · Rigenerazioni: {s?.regenerate_draft ?? 0}</div>
                            <div className="text-xs text-brand-navy/60">OK: {s?.success ?? 0} · Falliti: {s?.failed ?? 0}</div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-brand-navy/40">Token e costo/crediti: Non disponibile dal provider (conteggio delle sole chiamate reali).</p>
                      {(usage.email_assistant.preview_test?.total_calls ?? 0) > 0 && (
                        <div data-testid="ai-email-preview-test" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                          Ambiente preview/test: <b>{usage.email_assistant.preview_test.total_calls}</b> chiamate reali (crediti preview consumati) — separate e NON incluse nei totali di produzione.
                        </div>
                      )}
                    </div>
                  )}

                  {usage.budget?.exceeded && (
                    <div data-testid="ai-budget-alert-exceeded" className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                      <div className="text-sm text-red-800">
                        <p className="font-semibold">{t("admin.aiBudgetExceeded")}</p>
                        <p className="mt-0.5">€{Number(usage.budget.month_cost_eur).toFixed(4)} {t("admin.aiBudgetOf")} €{Number(usage.budget.monthly_budget_eur).toFixed(2)} ({usage.budget.percent}%) · {usage.budget.month}</p>
                      </div>
                    </div>
                  )}
                  {usage.budget?.near && !usage.budget?.exceeded && (
                    <div data-testid="ai-budget-alert-near" className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-semibold">{t("admin.aiBudgetNear")}</p>
                        <p className="mt-0.5">€{Number(usage.budget.month_cost_eur).toFixed(4)} {t("admin.aiBudgetOf")} €{Number(usage.budget.monthly_budget_eur).toFixed(2)} ({usage.budget.percent}%)</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 rounded-2xl border border-brand-navy/10 bg-white p-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-heading font-bold text-brand-navy">{t("admin.aiBudget")}</p>
                        <p className="mt-0.5 text-sm text-brand-navy/55 max-w-lg">{t("admin.aiBudgetHint")}</p>
                      </div>
                      <div className="flex items-end gap-2">
                        <div>
                          <Label className="text-xs text-brand-navy/50">{t("admin.aiBudgetSet")}</Label>
                          <Input data-testid="ai-budget-input" type="number" step="0.5" min="0" value={budgetForm}
                            onChange={(e) => setBudgetForm(e.target.value)} placeholder="0.00" className="w-32 mt-1" />
                        </div>
                        <Button data-testid="ai-budget-save" onClick={saveBudget} disabled={budgetSaving}
                          className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">
                          {budgetSaving ? t("common.loading") : t("admin.save")}
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-brand-navy/60">{t("admin.aiBudgetMonth")} ({usage.budget.month})</span>
                        <span data-testid="ai-budget-monthcost" className="font-semibold text-brand-navy">
                          €{Number(usage.budget.month_cost_eur).toFixed(4)}
                          {usage.budget.monthly_budget_eur > 0 && <span className="text-brand-navy/40"> / €{Number(usage.budget.monthly_budget_eur).toFixed(2)}</span>}
                        </span>
                      </div>
                      {usage.budget.monthly_budget_eur > 0 ? (
                        <>
                          <div className="mt-2 h-2.5 w-full rounded-full bg-brand-navy/10 overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${usage.budget.exceeded ? "bg-red-500" : usage.budget.near ? "bg-amber-500" : "bg-brand-electric"}`}
                              style={{ width: `${Math.min(100, usage.budget.percent)}%` }} />
                          </div>
                          {!usage.budget.exceeded && !usage.budget.near && (
                            <p className="mt-2 flex items-center gap-1.5 text-xs text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> {t("admin.aiBudgetOk")}</p>
                          )}
                        </>
                      ) : (
                        <p className="mt-2 text-xs text-brand-navy/45">{t("admin.aiBudgetNone")}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div data-testid="ai-cvcount" className="rounded-2xl border border-brand-navy/10 bg-white p-5">
                      <p className="text-xs uppercase tracking-wide text-brand-navy/50">{t("admin.aiCvGenerated")}</p>
                      <p className="mt-1.5 font-heading font-extrabold text-2xl text-brand-navy">{usage.generation.count}</p>
                    </div>
                    <div data-testid="ai-totalcost" className="rounded-2xl border border-brand-navy/10 bg-white p-5">
                      <p className="text-xs uppercase tracking-wide text-brand-navy/50">{t("admin.aiTotalCost")}</p>
                      <p className="mt-1.5 font-heading font-extrabold text-2xl text-brand-electric">€{Number(usage.total_cost_eur).toFixed(4)}</p>
                    </div>
                    <div data-testid="ai-avgcost" className="rounded-2xl border border-brand-navy/10 bg-white p-5">
                      <p className="text-xs uppercase tracking-wide text-brand-navy/50">{t("admin.aiAvgPerCv")}</p>
                      <p className="mt-1.5 font-heading font-extrabold text-2xl text-brand-orange">€{Number(usage.avg_cost_per_cv_eur).toFixed(4)}</p>
                    </div>
                    <div data-testid="ai-avgtokens" className="rounded-2xl border border-brand-navy/10 bg-white p-5">
                      <p className="text-xs uppercase tracking-wide text-brand-navy/50">{t("admin.aiAvgTokens")}</p>
                      <p className="mt-1.5 font-heading font-extrabold text-2xl text-brand-navy">{usage.avg_tokens_per_cv}</p>
                    </div>
                  </div>

                  {usage.margin && (
                    <div data-testid="ai-margin" className="mt-6 rounded-2xl border border-brand-navy/10 bg-gradient-to-br from-brand-navy to-[#0e1834] text-white p-5">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-heading font-bold">{t("admin.aiMargin")}</p>
                          <p className="mt-0.5 text-sm text-white/55 max-w-lg">{t("admin.aiMarginNote")}</p>
                        </div>
                        <div className="text-right">
                          <p data-testid="ai-margin-net" className="font-heading font-extrabold text-3xl text-brand-orange">{usage.margin.symbol}{Number(usage.margin.net_profit).toFixed(2)}</p>
                          <span className="inline-block mt-1 text-xs font-semibold text-brand-orange bg-brand-orange/15 rounded-full px-2 py-0.5">{usage.margin.margin_percent}% {t("admin.aiMarginNet")}</span>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center gap-2 text-sm flex-wrap">
                        <span className="rounded-lg bg-white/10 px-3 py-1.5">{t("admin.aiMarginPrice")}: <b>{usage.margin.symbol}{Number(usage.margin.price).toFixed(2)}</b></span>
                        <span className="text-white/40">−</span>
                        <span className="rounded-lg bg-white/10 px-3 py-1.5">{t("admin.aiMarginAi")}: <b>{usage.margin.symbol}{Number(usage.margin.ai_cost).toFixed(4)}</b></span>
                        <span className="text-white/40">−</span>
                        <span className="rounded-lg bg-white/10 px-3 py-1.5">{t("admin.aiMarginStripe")}: <b>{usage.margin.symbol}{Number(usage.margin.stripe_fee).toFixed(2)}</b> <span className="text-white/40 text-xs">({usage.margin.stripe_percent}% + {usage.margin.symbol}{Number(usage.margin.stripe_fixed).toFixed(2)})</span></span>
                        <span className="text-brand-orange font-bold">=</span>
                        <span className="rounded-lg bg-brand-orange/20 px-3 py-1.5 font-semibold">{usage.margin.symbol}{Number(usage.margin.net_profit).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 grid lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 rounded-2xl border border-brand-navy/10 bg-white p-5">
                      <p className="font-heading font-bold text-brand-navy">{t("admin.aiByMode")}</p>
                      <div className="mt-3 space-y-2">
                        {Object.keys(usage.by_mode).length === 0 ? (
                          <p className="text-sm text-brand-navy/50">{t("admin.aiNoData")}</p>
                        ) : Object.entries(usage.by_mode).map(([m, v]) => (
                          <div key={m} data-testid={`ai-mode-${m}`} className="flex items-center justify-between gap-3 rounded-lg border border-brand-navy/10 px-3 py-2">
                            <span className="text-sm font-medium text-brand-navy">{t(`admin.mode${m.charAt(0).toUpperCase() + m.slice(1)}`)}</span>
                            <span className="text-xs text-brand-navy/50">{v.count} CV · {v.total_tokens} tok</span>
                            <span className="text-sm font-semibold text-brand-electric">€{Number(v.avg_cost_eur).toFixed(4)}/CV</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-brand-navy/10 bg-white p-5">
                      <p className="font-heading font-bold text-brand-navy">{t("admin.aiOcr")}</p>
                      <p className="mt-3 text-sm text-brand-navy/70">{usage.ocr.count} · <span className="font-semibold">€{Number(usage.ocr.cost_eur).toFixed(4)}</span></p>
                      <p className="mt-4 text-[11px] leading-relaxed text-brand-navy/45">
                        {t("admin.aiRatesNote")}: {usage.rates.model} · in ${usage.rates.input_usd_per_1m}/1M · out ${usage.rates.output_usd_per_1m}/1M · USD→EUR {usage.rates.usd_to_eur}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl border border-brand-navy/10 bg-white p-5">
                    <p className="font-heading font-bold text-brand-navy">{t("admin.aiRecent")}</p>
                    {usage.recent.length === 0 ? (
                      <p className="mt-3 text-sm text-brand-navy/50">{t("admin.aiNoData")}</p>
                    ) : (
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-brand-navy/50 border-b border-brand-navy/10">
                              <th className="py-2 pr-3 font-medium">{t("admin.aiMode")}</th>
                              <th className="py-2 pr-3 font-medium">{t("admin.aiTokens")}</th>
                              <th className="py-2 pr-3 font-medium">{t("admin.aiCost")}</th>
                              <th className="py-2 pr-3 font-medium">{t("admin.aiWhen")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usage.recent.map((r, i) => (
                              <tr key={i} data-testid={`ai-recent-${i}`} className="border-b border-brand-navy/5">
                                <td className="py-2 pr-3 text-brand-navy">{r.feature === "flyer_logo" ? (r.action === "revise" ? "Logo (rigenerazione)" : "Logo (creazione)") : r.feature === "flyer_generate" ? `Flyer ${r.action === "creative_compare" ? "confronto" : "gen"} (${r.model || ""})` : r.feature === "flyer_modify" ? "Modifica Flyer" : r.kind === "ocr" ? "OCR" : t(`admin.mode${(r.mode || "scratch").charAt(0).toUpperCase() + (r.mode || "scratch").slice(1)}`)}</td>
                                <td className="py-2 pr-3 text-brand-navy/60">{(r.feature === "flyer_logo" || r.feature === "flyer_generate" || r.feature === "flyer_modify") ? `${r.images || 0} img` : `${r.input_tokens} / ${r.output_tokens}`}</td>
                                <td className="py-2 pr-3 font-semibold text-brand-electric">€{Number(r.cost_eur).toFixed(4)}{r.estimated ? "*" : ""}</td>
                                <td className="py-2 pr-3 text-brand-navy/50">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="mt-2 text-[11px] text-brand-navy/40">* costo stimato (usato solo se il provider non restituisce l'usage). I costi logo senza asterisco sono reali, calcolati dall'usage di OpenAI gpt-image-1 e convertiti in EUR.</p>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* EMAIL ASSISTANT */}
          <TabsContent value="email" className="mt-6">
            <EmailAssistant />
          </TabsContent>

          {/* PROGETTI FLYER */}
          <TabsContent value="flyer" className="mt-6">
            <div data-testid="admin-create-onbehalf-card" className="mb-5 rounded-2xl border border-brand-navy/10 bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-heading font-bold text-brand-navy text-lg">Crea come amministratore</p>
                <p className="text-sm text-brand-navy/60">Creazione per conto del cliente</p>
                <p className="text-xs text-brand-navy/45 mt-1">Non verrà effettuato alcun pagamento Stripe. Verranno utilizzati soltanto i crediti necessari alla generazione.</p>
              </div>
              <Button data-testid="admin-create-onbehalf-btn" onClick={() => { window.location.href = "/social-flyer-creator?admin=1"; }}
                className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-2 shrink-0"><Layers className="h-4 w-4" /> Crea come amministratore</Button>
            </div>
            <FlyerMonitor />
            <AdminFlyerProjects />
          </TabsContent>

        </Tabs>
      </div>

      <ProductDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={() => { loadAll(); setProductReload((k) => k + 1); }} categories={categories} editing={editing} />

    </div>
  );
};

export default Admin;
